import { useEffect, useState } from "react";

import { useAtom } from "jotai";
import { BottomSheet } from "./BottomSheet";
import { SortPanel } from "./SortPanel";
import {
    useSearchFilters,
    type SortValue,
} from "./useSearchFilters";
import { isSortSheetOpenAtom } from "./store/sortFilterAtoms";

// Mobile-only Sort sheet. Selections are held in local state until Apply,
// which commits them to the URL via useSearchFilters.
export const SortSheet = () => {
    const [isOpen, setIsOpen] = useAtom(isSortSheetOpenAtom);
    // The atom is module-level and the app has one store, so an open sheet
    // would otherwise survive a route change and remount unprompted on the
    // next page that renders this component — backdrop, scroll lock and focus
    // trap included. The sheet closes on its own way out. (#401 review R6.)
    useEffect(() => () => setIsOpen(false), [setIsOpen]);
    if (!isOpen) return null;
    return <SortSheetContent />;
};

const SortSheetContent = () => {
    const [, setIsOpen] = useAtom(isSortSheetOpenAtom);
    const filters = useSearchFilters();
    const [localSort, setLocalSort] = useState<SortValue | null>(filters.sort);
    // RE-seeded whenever the URL's sort changes underneath the open sheet, as
    // FilterSheet re-seeds its draft: a browser Back does not close the sheet,
    // and a buffer still holding the previous URL's sort would re-write on
    // Apply the very value the Back removed. During render, not in an effect
    // (an effect is a commit late, and this repo's lint forbids it): a new
    // URL value is a navigation, and nothing else writes it while the sheet is
    // up. Keyed on a string-or-null, so `!==` is safe.
    const [seededSort, setSeededSort] = useState<SortValue | null>(filters.sort);
    if (seededSort !== filters.sort) {
        setSeededSort(filters.sort);
        setLocalSort(filters.sort);
    }

    const close = () => setIsOpen(false);
    const apply = () => {
        filters.setSort(localSort);
        close();
    };

    return (
        <BottomSheet
            title="Sort By"
            testId="sort-sheet"
            onClose={close}
            onApply={apply}
        >
            <SortPanel
                selected={localSort}
                onChange={setLocalSort}
                showHeading={false}
            />
        </BottomSheet>
    );
};
